import trI18n from '@/common/utils/trI18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import Logger from '@/common/utils/Logger';
import useTranslationSubscription from '@/hooks/useTranslation';

const MEDIA_DEVICE_CHANGE_EVENT = 'devicechange';
const APP_INTEGRATION_AUDIO_DEVICE_STORAGE_PREFIX = 'appIntegrationAudioDevices';
const BROWSER_AUDIO_DEVICE_ALIAS_IDS = ['default', 'communications'];

function useAppIntegrationAudioDevices({
    corporationId,
    isDeviceChangeListenerActive = false,
    projectId,
    userId,
} = {}) {
    useTranslationSubscription();

    const [appIntegrationDevices, setAppIntegrationDevices] = useState([]);
    const [isLoadingAppIntegrationDevices, setIsLoadingAppIntegrationDevices] = useState(false);
    const [isAppIntegrationMicPermissionGranted, setIsAppIntegrationMicPermissionGranted] = useState(false);
    const [appIntegrationDeviceErrorKey, setAppIntegrationDeviceErrorKey] = useState('');
    const [appIntegrationSelectionErrorKey, setAppIntegrationSelectionErrorKey] = useState('');
    const [customerAudioDeviceId, setCustomerAudioDeviceId] = useState('');
    const [userAudioDeviceId, setUserAudioDeviceId] = useState('');
    const appIntegrationMediaStreamRef = useRef(null);
    const appIntegrationDeviceIdsRef = useRef([]);
    const hasDisconnectedSelectionErrorRef = useRef(false);
    const isAppIntegrationMicPermissionGrantedRef = useRef(false);
    const customerAudioDeviceIdRef = useRef(customerAudioDeviceId);
    customerAudioDeviceIdRef.current = customerAudioDeviceId;
    const userAudioDeviceIdRef = useRef(userAudioDeviceId);
    userAudioDeviceIdRef.current = userAudioDeviceId;

    // No useMemo: must recompute on every render (even if the key is unchanged)
    // because useTranslationSubscription() only bumps a Recoil atom internally
    // (no version value exposed) to trigger re-render once the dictionary loads.
    // A useMemo keyed on the key alone would keep the stale MISS translation forever.
    const appIntegrationDeviceError = appIntegrationDeviceErrorKey ? trI18n(appIntegrationDeviceErrorKey) : '';
    const appIntegrationSelectionError = appIntegrationSelectionErrorKey
        ? trI18n(appIntegrationSelectionErrorKey)
        : '';

    const appIntegrationAudioDeviceStorageKey = corporationId && projectId && userId
        ? `${APP_INTEGRATION_AUDIO_DEVICE_STORAGE_PREFIX}:${corporationId}:${projectId}:${userId}`
        : '';

    const stopAppIntegrationMediaStream = useCallback(() => {
        if (appIntegrationMediaStreamRef.current) {
            appIntegrationMediaStreamRef.current.getTracks().forEach((track) => track.stop());
            appIntegrationMediaStreamRef.current = null;
        }
    }, []);

    const resetAppIntegrationLoadingState = useCallback(() => {
        setIsLoadingAppIntegrationDevices(false);
    }, []);

    const updateAppIntegrationMicPermissionGranted = useCallback((isGranted) => {
        isAppIntegrationMicPermissionGrantedRef.current = isGranted;
        setIsAppIntegrationMicPermissionGranted(isGranted);
    }, []);

    const normalizeAudioDeviceOption = useCallback((device, index) => ({
        deviceId: device?.deviceId || `audioinput-${index}`,
        label: device?.label || `Audio Input ${index + 1}`,
        groupId: device?.groupId || '',
    }), []);

    const updateAppIntegrationSelectionError = useCallback((messageKey) => {
        hasDisconnectedSelectionErrorRef.current =
            messageKey === 'APP_INTEGRATION_DEVICE_DISCONNECTED';
        setAppIntegrationSelectionErrorKey(messageKey);
    }, []);

    const clearAppIntegrationSelectionError = useCallback(() => {
        hasDisconnectedSelectionErrorRef.current = false;
        setAppIntegrationSelectionErrorKey('');
    }, []);

    const clearAppIntegrationSelectionErrorIfAllowed = useCallback(() => {
        if (!hasDisconnectedSelectionErrorRef.current) {
            setAppIntegrationSelectionErrorKey('');
        }
    }, []);

    const getSavedAppIntegrationDevices = useCallback(() => {
        if (!appIntegrationAudioDeviceStorageKey || typeof localStorage === 'undefined') return null;

        try {
            const saved = localStorage.getItem(appIntegrationAudioDeviceStorageKey);
            return saved ? JSON.parse(saved) : null;
        } catch (error) {
            Logger.error('getSavedAppIntegrationDevices error >>>', error);
            return null;
        }
    }, [appIntegrationAudioDeviceStorageKey]);

    const resetAppIntegrationAudioDeviceSelection = useCallback(() => {
        setCustomerAudioDeviceId('');
        setUserAudioDeviceId('');
        clearAppIntegrationSelectionError();
    }, [clearAppIntegrationSelectionError]);

    const applyDefaultSelection = useCallback((audioInputDevices, {
        hasDisconnectedSelectedDevice = false,
        preserveCurrentSelection = false,
    } = {}) => {
        const currentDeviceIds = audioInputDevices.map((device) => device.deviceId);

        if (preserveCurrentSelection) {
            const currentCustomer = customerAudioDeviceIdRef.current;
            const currentUser = userAudioDeviceIdRef.current;
            const nextCustomerAudioDeviceId = currentDeviceIds.includes(currentCustomer)
                ? currentCustomer
                : '';
            const nextUserAudioDeviceId = currentDeviceIds.includes(currentUser)
                ? currentUser
                : '';

            setCustomerAudioDeviceId(nextCustomerAudioDeviceId);
            setUserAudioDeviceId(nextUserAudioDeviceId);
            if (nextCustomerAudioDeviceId && nextUserAudioDeviceId &&
                nextCustomerAudioDeviceId === nextUserAudioDeviceId) {
                updateAppIntegrationSelectionError('APP_INTEGRATION_SAME_DEVICE_ERROR');
            } else if (hasDisconnectedSelectedDevice) {
                updateAppIntegrationSelectionError('APP_INTEGRATION_DEVICE_DISCONNECTED');
            } else {
                clearAppIntegrationSelectionErrorIfAllowed();
            }
            return;
        }

        const savedSelection = getSavedAppIntegrationDevices();
        const savedCustomerAudioDeviceId = currentDeviceIds.includes(savedSelection?.customerAudioDeviceId)
            ? savedSelection.customerAudioDeviceId
            : '';
        const savedUserAudioDeviceId = currentDeviceIds.includes(savedSelection?.userAudioDeviceId)
            ? savedSelection.userAudioDeviceId
            : '';
        const hasInvalidSameDeviceSelection = savedCustomerAudioDeviceId && savedUserAudioDeviceId &&
            savedCustomerAudioDeviceId === savedUserAudioDeviceId;

        setCustomerAudioDeviceId(hasInvalidSameDeviceSelection ? '' : savedCustomerAudioDeviceId);
        setUserAudioDeviceId(hasInvalidSameDeviceSelection ? '' : savedUserAudioDeviceId);
        clearAppIntegrationSelectionError();
    }, [
        clearAppIntegrationSelectionError,
        clearAppIntegrationSelectionErrorIfAllowed,
        getSavedAppIntegrationDevices,
        updateAppIntegrationSelectionError,
    ]);

    const saveAppIntegrationDevices = useCallback(({
        customerAudioDeviceId: selectedCustomerAudioDeviceId,
        userAudioDeviceId: selectedUserAudioDeviceId,
    }) => {
        if (!appIntegrationAudioDeviceStorageKey || typeof localStorage === 'undefined') return false;

        try {
            localStorage.setItem(appIntegrationAudioDeviceStorageKey, JSON.stringify({
                customerAudioDeviceId: selectedCustomerAudioDeviceId,
                userAudioDeviceId: selectedUserAudioDeviceId,
            }));
            return true;
        } catch (error) {
            Logger.error('saveAppIntegrationDevices error >>>', error);
            return false;
        }
    }, [appIntegrationAudioDeviceStorageKey]);

    const requestAppIntegrationMicrophonePermission = useCallback(async () => {
        const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
        if (!mediaDevices?.getUserMedia) {
            updateAppIntegrationMicPermissionGranted(false);
            setAppIntegrationDeviceErrorKey('APP_INTEGRATION_MIC_PERMISSION_ERROR');
            return false;
        }

        try {
            stopAppIntegrationMediaStream();
            const stream = await mediaDevices.getUserMedia({ audio: true });
            appIntegrationMediaStreamRef.current = stream;
            updateAppIntegrationMicPermissionGranted(true);
            setAppIntegrationDeviceErrorKey('');
            return true;
        } catch (error) {
            Logger.error('requestAppIntegrationMicrophonePermission error >>>', error);
            updateAppIntegrationMicPermissionGranted(false);
            setAppIntegrationDevices([]);
            setCustomerAudioDeviceId('');
            setUserAudioDeviceId('');
            setAppIntegrationDeviceErrorKey('APP_INTEGRATION_MIC_PERMISSION_ERROR');
            clearAppIntegrationSelectionError();
            return false;
        } finally {
            stopAppIntegrationMediaStream();
        }
    }, [
        clearAppIntegrationSelectionError,
        stopAppIntegrationMediaStream,
        updateAppIntegrationMicPermissionGranted,
    ]);

    const loadAppIntegrationAudioDevices = useCallback(async ({ isDeviceChangeRefresh = false } = {}) => {
        const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
        setIsLoadingAppIntegrationDevices(true);

        try {
            const hasPermission = isDeviceChangeRefresh &&
                isAppIntegrationMicPermissionGrantedRef.current
                ? true
                : await requestAppIntegrationMicrophonePermission();
            if (!hasPermission) {
                appIntegrationDeviceIdsRef.current = [];
                return;
            }

            if (!mediaDevices?.enumerateDevices) {
                updateAppIntegrationMicPermissionGranted(false);
                setAppIntegrationDevices([]);
                setCustomerAudioDeviceId('');
                setUserAudioDeviceId('');
                setAppIntegrationDeviceErrorKey('APP_INTEGRATION_MIC_PERMISSION_ERROR');
                clearAppIntegrationSelectionError();
                appIntegrationDeviceIdsRef.current = [];
                return;
            }

            const devices = await mediaDevices.enumerateDevices();
            const audioInputDevices = devices
                .filter((device) => (
                    device.kind === 'audioinput' &&
                    !BROWSER_AUDIO_DEVICE_ALIAS_IDS.includes(device.deviceId)
                ))
                .map(normalizeAudioDeviceOption);
            const previousDeviceIds = appIntegrationDeviceIdsRef.current;
            const currentDeviceIds = audioInputDevices.map((device) => device.deviceId);
            const selectedDeviceIds = [
                customerAudioDeviceIdRef.current,
                userAudioDeviceIdRef.current,
            ].filter(Boolean);
            const hasDisconnectedSelectedDevice = isDeviceChangeRefresh &&
                selectedDeviceIds.some((deviceId) => (
                    previousDeviceIds.includes(deviceId) &&
                    !currentDeviceIds.includes(deviceId)
                ));

            setAppIntegrationDevices(audioInputDevices);
            appIntegrationDeviceIdsRef.current = currentDeviceIds;

            if (audioInputDevices.length === 0) {
                setCustomerAudioDeviceId('');
                setUserAudioDeviceId('');
                if (hasDisconnectedSelectedDevice || hasDisconnectedSelectionErrorRef.current) {
                    setAppIntegrationDeviceErrorKey('');
                    updateAppIntegrationSelectionError('APP_INTEGRATION_DEVICE_DISCONNECTED');
                } else {
                    setAppIntegrationDeviceErrorKey('APP_INTEGRATION_NO_DEVICE');
                    clearAppIntegrationSelectionError();
                }
                return;
            }

            setAppIntegrationDeviceErrorKey('');
            applyDefaultSelection(audioInputDevices, {
                hasDisconnectedSelectedDevice,
                preserveCurrentSelection: isDeviceChangeRefresh,
            });
        } catch (error) {
            Logger.error('loadAppIntegrationAudioDevices error >>>', error);
            if (isDeviceChangeRefresh) return;

            updateAppIntegrationMicPermissionGranted(false);
            setAppIntegrationDevices([]);
            setCustomerAudioDeviceId('');
            setUserAudioDeviceId('');
            setAppIntegrationDeviceErrorKey('APP_INTEGRATION_MIC_PERMISSION_ERROR');
            clearAppIntegrationSelectionError();
            appIntegrationDeviceIdsRef.current = [];
        } finally {
            setIsLoadingAppIntegrationDevices(false);
        }
    }, [
        applyDefaultSelection,
        clearAppIntegrationSelectionError,
        normalizeAudioDeviceOption,
        requestAppIntegrationMicrophonePermission,
        updateAppIntegrationSelectionError,
        updateAppIntegrationMicPermissionGranted,
    ]);

    useEffect(() => {
        if (!isDeviceChangeListenerActive) return;

        const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
        if (!mediaDevices) return;

        const handleDeviceChange = () => {
            loadAppIntegrationAudioDevices({ isDeviceChangeRefresh: true });
        };

        if (typeof mediaDevices.addEventListener === 'function') {
            mediaDevices.addEventListener(MEDIA_DEVICE_CHANGE_EVENT, handleDeviceChange);
            return () => {
                mediaDevices.removeEventListener(MEDIA_DEVICE_CHANGE_EVENT, handleDeviceChange);
            };
        }

        const previousOnDeviceChange = mediaDevices.ondevicechange;
        mediaDevices.ondevicechange = handleDeviceChange;
        return () => {
            if (mediaDevices.ondevicechange === handleDeviceChange) {
                mediaDevices.ondevicechange = previousOnDeviceChange;
            }
        };
    }, [isDeviceChangeListenerActive, loadAppIntegrationAudioDevices]);

    useEffect(() => {
        return () => {
            stopAppIntegrationMediaStream();
        };
    }, [stopAppIntegrationMediaStream]);

    return {
        appIntegrationDevices,
        isLoadingAppIntegrationDevices,
        isAppIntegrationMicPermissionGranted,
        appIntegrationDeviceError,
        appIntegrationSelectionError,
        customerAudioDeviceId,
        clearAppIntegrationSelectionError,
        saveAppIntegrationDevices,
        updateAppIntegrationSelectionError,
        userAudioDeviceId,
        setCustomerAudioDeviceId,
        setUserAudioDeviceId,
        resetAppIntegrationAudioDeviceSelection,
        loadAppIntegrationAudioDevices,
        stopAppIntegrationMediaStream,
        resetAppIntegrationLoadingState,
    };
}

export default useAppIntegrationAudioDevices;
